import { apiLoginIsiPrint, ApiLoginIsiPrintResponse } from '../api/apiLoginIsiPrint.ts'
import {
  AgentManifest,
  IsiPrinterClient,
  JobInfo,
  PrinterError,
  PrinterInfo,
  PrintTarget,
} from '../sdk/isi-printer-client.ts'

/**
 * Integración con isi-print (agente local de impresión, SDK `isi-printer-client`).
 * Toda conexión con isi-print debe pasar por `isiPrint` o por el hook `useIsiPrint`.
 */

/** URL del agente en el equipo del cajero */
export const ISI_PRINT_URL = (
  (import.meta.env as Record<string, string | undefined>).ISI_PRINT_URL || 'http://localhost:19100'
).replace(/\/+$/, '')

/** Panel local del agente: autorizar sitios, ver impresoras y licencia */
export const ISI_PRINT_PANEL_URL = `${ISI_PRINT_URL}/`

/** Manifiesto publicado junto a los instaladores */
export const ISI_PRINT_MANIFEST_URL = 'https://descargas.isipaid.net/isi-print/latest.json'

/** Carpeta pública de descargas, respaldo si no se puede leer el manifiesto */
export const ISI_PRINT_DESCARGAS_URL = 'https://descargas.isipaid.net/isi-print/'

/** Última respuesta de `loginIsiPrint` (vencimiento y mensajes de la licencia del comercio) */
let ultimoLogin: ApiLoginIsiPrintResponse | null = null
export const getUltimoLoginIsiPrint = () => ultimoLogin

/**
 * Cliente único de isi-print. El token de licencia lo emite el backend (`loginIsiPrint`)
 * para el comercio de la sesión; el SDK lo instala, lo renueva antes de vencer y reintenta
 * si el agente responde LICENSE_REQUIRED / LICENSE_EXPIRED.
 */
export const isiPrint = new IsiPrinterClient({
  baseUrl: ISI_PRINT_URL,
  getLicenseToken: async () => {
    const login = await apiLoginIsiPrint()
    ultimoLogin = login
    if (!login.autorizado || !login.token) {
      throw new PrinterError(
        login.mensaje || 'El comercio no tiene una licencia de isi-print vigente',
        'LICENSE_NOT_AUTHORIZED',
      )
    }
    return login.token
  },
})

/** Estado de isi-print en este equipo */
export type IsiPrintEstado =
  /** Consultando el agente */
  | 'VERIFICANDO'
  /** El agente no respondió: no está instalado o no está en ejecución */
  | 'NO_INSTALADO'
  /** Teléfono, tableta o ChromeOS: isi-print no se puede instalar (ver services/dispositivo.ts) */
  | 'NO_SOPORTADO'
  /** El cajero aún no autorizó este sitio en el agente */
  | 'PENDIENTE_AUTORIZAR'
  /** La versión instalada es menor a la mínima publicada */
  | 'ACTUALIZACION_REQUERIDA'
  /** Sin licencia de impresión del comercio o no se pudo instalar en el agente */
  | 'SIN_LICENCIA'
  /** Listo para imprimir */
  | 'LISTO'

export const ISI_PRINT_ESTADO_LABEL: Record<IsiPrintEstado, string> = {
  VERIFICANDO: 'Buscando isi-print…',
  NO_INSTALADO: 'isi-print no instalado',
  NO_SOPORTADO: 'Impresión directa no disponible en este dispositivo',
  PENDIENTE_AUTORIZAR: 'isi-print pendiente de autorizar',
  ACTUALIZACION_REQUERIDA: 'isi-print requiere actualización',
  SIN_LICENCIA: 'isi-print sin licencia',
  LISTO: 'isi-print conectado',
}

/** Estado de la impresora informado por el agente */
export type IsiPrinterStatus = PrinterInfo['status']

export const ISI_PRINTER_STATUS_LABEL: Record<IsiPrinterStatus, string> = {
  online: 'Lista',
  offline: 'Apagada o desconectada',
  paper_out: 'Sin papel',
  paper_jam: 'Papel atascado',
  door_open: 'Tapa abierta',
  error: 'Con error',
  paused: 'En pausa',
}

/** Valor guardado en `impresora.atributo1`: ID de hardware, nombre del sistema o IP:puerto */
const IP_PUERTO = /^(\d{1,3}\.){3}\d{1,3}(:\d{1,5})?$/

/** Opciones de envío: anti-duplicado y reimpresión intencional */
export interface IsiPrintEnvioOpciones {
  /**
   * Clave anti-duplicado (ej. `factura-<id>`): otro envío con la misma clave dentro de la ventana no
   * se reimprime si el anterior está en cola, imprimiendo o impreso (sí si falló o se canceló).
   */
  idempotencyKey?: string
  /** Ventana de la clave en segundos (5–1800). Por defecto la del agente (60) */
  idempotencyWindow?: number
  /** Reimpresión intencional: imprime aunque haya un duplicado reciente */
  force?: boolean
}

/** Destino para el SDK a partir del valor guardado, con las opciones de envío */
export const isiPrintTarget = (valor: string, opciones: IsiPrintEnvioOpciones = {}): PrintTarget => {
  const v = valor.trim()
  const base: PrintTarget = IP_PUERTO.test(v)
    ? { printer: v.includes(':') ? v : `${v}:9100`, connection: 'network' }
    : { printer: v }
  if (opciones.idempotencyKey) base.idempotency_key = opciones.idempotencyKey
  if (opciones.idempotencyWindow) base.idempotency_window = opciones.idempotencyWindow
  if (opciones.force) base.force = true
  return base
}

/** Motivo de un trabajo detenido o fallido (JobInfo.reason) */
export const ISI_JOB_REASON_LABEL: Record<string, string> = {
  PAPER_OUT: 'sin papel',
  OFFLINE: 'apagada o desconectada',
  DOOR_OPEN: 'tapa abierta',
  PAPER_JAM: 'papel atascado',
  USER_INTERVENTION: 'requiere atención',
  PRINTER_UNREACHABLE: 'no responde',
  CANCELLED_BY_CLIENT: 'cancelado',
}

/** Texto legible del estado de un trabajo, ej. "sin papel" */
export const mensajeTrabajoIsiPrint = (job: JobInfo): string =>
  (job.reason && ISI_JOB_REASON_LABEL[job.reason]) ||
  job.message ||
  (job.status === 'cancelled' ? 'cancelado' : job.status === 'error' ? 'error de impresión' : job.status)

/** El trabajo tiene un problema: falló, se canceló o está detenido con motivo (ej. PAPER_OUT) */
export const trabajoConProblema = (job: JobInfo): boolean =>
  job.status === 'error' || job.status === 'cancelled' || (!job.final && !!job.reason)

export type ResultadoTrabajo =
  /** Salió impreso */
  | { estado: 'impreso'; job: JobInfo }
  /** Falló, se canceló o quedó detenido (sin papel, apagada...) */
  | { estado: 'fallo'; job: JobInfo }
  /** Sigue en cola o imprimiendo sin problemas al vencer la espera */
  | { estado: 'pendiente'; job: JobInfo }

/**
 * Espera el resultado de un trabajo, pero a diferencia de `waitForJob` corta en cuanto aparece un
 * problema (un PAPER_OUT no definitivo también cuenta), para poder pasar a otra impresora.
 */
export const esperarTrabajoIsiPrint = async (
  jobId: string,
  { timeoutMs = 8_000, intervalMs = 500 }: { timeoutMs?: number; intervalMs?: number } = {},
): Promise<ResultadoTrabajo> => {
  const fin = Date.now() + timeoutMs
  let job = await isiPrint.getJob(jobId)
  for (;;) {
    if (job.status === 'printed') return { estado: 'impreso', job }
    if (trabajoConProblema(job)) return { estado: 'fallo', job }
    if (job.final || Date.now() >= fin) return { estado: job.final ? 'impreso' : 'pendiente', job }
    await new Promise((r) => setTimeout(r, intervalMs))
    job = await isiPrint.getJob(jobId)
  }
}

/** Cancela un trabajo sin lanzar error (ej. JOB_ALREADY_PRINTED). Devuelve el código de error, si hubo */
export const cancelarTrabajoIsiPrint = async (jobId: string): Promise<string | null> => {
  try {
    await isiPrint.cancelJob(jobId)
    return null
  } catch (e) {
    return e instanceof PrinterError ? (e.code ?? 'ERROR') : 'ERROR'
  }
}

/** Cómo se encontró la impresora guardada entre las detectadas */
export type IsiPrintCoincidencia = 'id' | 'nombre' | 'puerto'

/**
 * Busca la impresora guardada (ID, nombre o IP) entre las detectadas por el agente.
 * `coincidencia` distinto de 'id' indica un registro antiguo que conviene actualizar al ID.
 */
export const resolverImpresoraIsiPrint = (
  impresoras: PrinterInfo[],
  valor?: string | null,
): { impresora: PrinterInfo; coincidencia: IsiPrintCoincidencia } | null => {
  const v = (valor ?? '').trim().toLowerCase()
  if (!v) return null
  const porId = impresoras.find((p) => p.id.toLowerCase() === v)
  if (porId) return { impresora: porId, coincidencia: 'id' }
  const porNombre = impresoras.find((p) => p.name.trim().toLowerCase() === v)
  if (porNombre) return { impresora: porNombre, coincidencia: 'nombre' }
  const sinPuertoDefault = v.replace(/:9100$/, '')
  const porPuerto = impresoras.find((p) => {
    const port = p.port.trim().toLowerCase()
    return port === v || port.replace(/:9100$/, '') === sinPuertoDefault
  })
  return porPuerto ? { impresora: porPuerto, coincidencia: 'puerto' } : null
}

/** Mensaje legible de un error del agente */
export const mensajeIsiPrint = (error: unknown): string => {
  if (error instanceof PrinterError) {
    switch (error.code) {
      case 'ORIGIN_NOT_AUTHORIZED':
        return 'isi-print aún no autorizó este sitio. Acepte el aviso junto al reloj de Windows.'
      case 'PRINTER_NOT_FOUND':
        return error.suggestedMatch
          ? `No se encontró la impresora. ¿Es “${error.suggestedMatch}”?`
          : 'No se encontró la impresora en este equipo.'
      case 'LICENSE_REQUIRED':
      case 'LICENSE_EXPIRED':
      case 'LICENSE_INVALID':
      case 'LICENSE_NOT_AUTHORIZED':
        return error.message || 'La licencia de isi-print no está vigente.'
      default:
        return error.message
    }
  }
  if (error instanceof TypeError) return 'No se pudo conectar con isi-print. Verifique que esté en ejecución.'
  return error instanceof Error ? error.message : String(error ?? 'Error desconocido')
}

/** Lee el manifiesto de la última versión publicada (requiere CORS en el dominio de descargas) */
export const leerManifiestoIsiPrint = async (signal?: AbortSignal): Promise<AgentManifest> => {
  const res = await fetch(ISI_PRINT_MANIFEST_URL, { cache: 'no-store', signal })
  if (!res.ok) throw new Error(`No se pudo leer el manifiesto de isi-print (HTTP ${res.status})`)
  return (await res.json()) as AgentManifest
}

export { PrinterError }
export type { AgentManifest, JobInfo, PrinterInfo, PrintTarget }
